/**
 * ============================================================================
 * ⭐⭐ DRIVERS / CLIENTS / CARRIERS / CONSIGNEES — WHAT THE PANEL IS ABOUT
 * ============================================================================
 *
 * > Pete, 2026-09-19: *"Or maybe just like the status bar but have Drivers,
 * > Clients Carriers as the check boxes underneath the Driver Check-in"*
 *
 * Decision 188, §1AV.171. Restyled the same evening from his own screen — §1AV.173.
 *
 * ---- ⛔⛔ IT LOOKS LIKE THE STATUS BAR AND IT IS NOT A SET OF CHECKBOXES ------
 *
 * `All Status 2 · Requested 0 · Approved 0` is MULTI-SELECT: independent
 * checkboxes, and the ticks exist to say WHICH are on. This is ONE-OF-FOUR — the
 * card list, the calendar filter and the rail focus each take exactly one subject.
 *
 * ⚠️ AND MULTI-SELECT IS NOT MERELY UNWANTED, IT IS UNDER-SPECIFIED: with Drivers
 * *and* Clients on, the rail can still only focus on one thing, so hovering a
 * client card while driver cards are on screen leaves the `ALL` state with no
 * defined meaning. Single-select is not a simplification — it is the only reading
 * the rest of decision 188 already assumes.
 *
 * ---- ⛔⛔ SO WHAT TELLS THE READER IT IS ONE-OF-FOUR? THE `|`, NOT THE MARKER ---
 *
 * ⤴ THE FIRST CUT ANSWERED "the marker": no tick, no swatch, and the selected word
 * FILLED — `bg-foreground text-background`. The reasoning was
 * ** A CONTROL THAT LOOKS LIKE THE ONE BESIDE IT PROMISES THE BEHAVIOUR OF THE ONE
 *    BESIDE IT ** — borrow the status row's tick for a radio choice and the user
 * learns to try ticking two.
 *
 * ⛔⛔ PETE REFUSED IT ON SIGHT, AND THEN REFUSED HIS OWN FIRST REPLACEMENT:
 *   1. *"Text is way too big, font is too thick and tall. Needs to be like In Out
 *      and Trucks … I also don't like the inconsistency with the box around the
 *      word. It should be like our status bar with a check next to the selected one
 *      and a | inbetween them so you can tell they are different and they can be
 *      tightened together."*
 *   2. *"Maybe the box should stay around the tics cause it does match DAY and New
 *      Appointment buttons"* — and then, finally:
 *   3. *"leave the check next to the selected item but remove the box around the
 *      name (Drivers, Clients, Carriers, Consignees"*
 *
 * ⭐⭐⭐ **THE MARKER WAS NEVER THE THING CARRYING "PICK ONE". THE SEPARATOR IS.**
 * A tick says *this one is on*; it is the `|` — and the absence of one after the
 * last word — that says *these four are a set you choose from*. I spent the marker
 * to say something the punctuation says better, and paid for it with a black block
 * of text in a 340px panel that reads heavier than the panel's own heading.
 *
 * ⚠️ AND THE FILL WAS NEVER JUST "A STYLE". `bg-foreground text-background` is the
 * single highest-contrast pair the theme owns — the treatment the primary button
 * uses. Handing it to the DEFAULT state of a four-way selector made the resting
 * screen shout.
 *
 *   ** THE STRONGEST TREATMENT IN A THEME BELONGS TO THE RAREST STATE, NOT THE ONE
 *      THAT IS TRUE EVERY TIME THE SCREEN OPENS. **
 *
 * ---- ✅ WHAT IT IS NOW ------------------------------------------------------
 *
 *   ✓ Drivers │ Clients │ Carriers │ Consignees
 *
 * No box on any word, in any state. The selected one takes a `Check` and
 * `text-foreground`; the rest are `text-muted-foreground`.
 *
 * ⛔⤴ ~~`font-medium` / `font-normal`~~ — STRUCK the same evening
 * (§0.3). Pete: *"Text needs to be smaller. Is that the same size as In Out … they
 * all should be the same text size as Limits"*. ⚠️ Measuring first showed the chips
 * were ALREADY smaller than `In`/`Out` (11px vs 12px) and ALREADY the same size as
 * `Limits`. ⛔⛔ AND THEN A LIVE READ OF PETE'S SCREEN SHOWED WHY NONE OF IT HAD
 * WORKED: `button { font-size: 14px }` in index.css was beating every size class
 * the ROW carried, so the chips were 14px throughout while `Limits` — a span — sat
 * at 11px. ✅ The size is now on the BUTTON, and this row is **12px**, matching the
 * `In` / `Out` / `Trucks` counts Pete named for it. See ChipRow.tsx.
 *
 * ⛔ THE TICK IS `shrink-0` AND THE ROW RESERVES NO SPACE FOR IT. Only one word can
 * ever carry it, so the row's width changes by the tick's width as the subject
 * moves — accepted deliberately: reserving a gutter on all four would cost four
 * times the space in the panel this change exists to save.
 * ⚠️ `whitespace-nowrap` (from `chipButtonClass`) keeps a word and its own tick
 * together; `flex-wrap` on the row (from `chipRowClass`) breaks BETWEEN words.
 *
 * ---- ⛔ THE `|` IS NOT A CHARACTER IN A LABEL --------------------------------
 *
 * It is a sibling `<span aria-hidden>`. Put it inside the button text and it
 * becomes part of the accessible name ("Clients |"), part of the click target, and
 * part of anything that ever reads `SUBJECT_WORDS[s].chip` back out.
 *
 * ---- ⚠️ THIS ROW NOW SHARES A LINE WITH `[columns] [search] [⚠ n]` -----------
 *
 * ⤴ ~~"A ROW WHOSE ORDER IS LOAD-BEARING IS FULL, WHATEVER SPACE IS LEFT IN IT."~~
 * That rule was aimed at the wrong risk. The 2026-09-10 constraint is that the ⚠
 * sits on the same axis as every card's own ⚠ — i.e. **hard right**. Chips entering
 * from the LEFT of a `justify-between` row do not move it; only a chip placed
 * AFTER it would, and none is. See CheckInPanel.tsx for the wrapper that holds the
 * three controls together as one right-hand group.
 * ============================================================================
 */
import { Fragment } from 'react';
import { Check } from 'lucide-react';
import { chipRowClass, chipButtonClass } from '@/components/ChipRow';
import { PANEL_SUBJECTS, SUBJECT_WORDS, type PanelSubject } from '@/lib/panelSubject';

interface Props {
    subject: PanelSubject;
    onSubject: (s: PanelSubject) => void;
    /** Per-subject counts for the day, when known. Omitted renders no number. */
    counts?: Partial<Record<PanelSubject, number>>;
    dense?: boolean;
    className?: string;
}

export function SubjectChips({ subject, onSubject, counts, dense = true, className }: Props) {
    return (
        <div className={chipRowClass('subject', dense, className)} role="radiogroup" aria-label="Show">
            {PANEL_SUBJECTS.map((s, i) => {
                const active = s === subject;
                const n = counts?.[s];
                return (
                    <Fragment key={s}>
                        {/* ⛔ BETWEEN, NOT AFTER — a trailing `|` reads as a fifth
                            subject that failed to render. `i > 0` is the whole rule. */}
                        {i > 0 && (
                            <span aria-hidden="true" className="select-none text-muted-foreground/40">
                                |
                            </span>
                        )}
                        <button
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => onSubject(s)}
                            /* ⛔ NOT `disabled` WHEN ALREADY ACTIVE. A disabled control in a
                               radio group reads as unavailable rather than as chosen, and it
                               cannot be focused — which breaks arrow-key navigation through
                               the group for anyone not using a mouse. */
                            /* ⛔⛔ COLOUR AND THE TICK, NEVER WEIGHT. Pete, 2026-09-19:
                               *"they all should be the same text size as Limits"* — and
                               nothing in this row or the status row above carries a weight
                               class any more. `text-foreground` against
                               `text-muted-foreground` is the whole visual difference, and
                               the ✓ is what makes it unambiguous rather than merely darker.
                               ** IF ONE ITEM IN A ROW IS HEAVIER, THE ROW READS AS TWO
                                  SIZES OF TEXT — WHICH IS EXACTLY WHAT WAS REPORTED. ** */
                            className={chipButtonClass(
                                'subject',
                                dense,
                                active
                                    ? 'text-foreground'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {/* ⛔ THE TICK IS THE ONLY MARKER, so it may not be subtle. It
                                takes the text colour rather than the status row's
                                `text-white`, because there is no coloured swatch behind
                                it to contrast against. */}
                            {/* ⚠️ `h-2.5` = 10px, just under the row's 11px. At `h-3` (12px)
                                the tick was TALLER THAN THE WORD beside it — its own kind of
                                "too big", and one that would have survived every font-size
                                change untouched.
                                ⛔ THE RULE IS `tick <= row font size`, NOT a fixed pairing:
                                a marker that out-measures its label stops reading as an
                                annotation and starts competing with it.
                                ** A GLYPH SIZED IN A DIFFERENT UNIT FROM THE TEXT IT
                                   ANNOTATES STOPS MATCHING IT THE FIRST TIME EITHER MOVES. ** */}
                            {active && <Check className="h-2.5 w-2.5 shrink-0" strokeWidth={3} />}
                            {/* ⛔⛔ THE WORDS COME FROM `SUBJECT_WORDS`, NEVER FROM A LITERAL
                                HERE. The chip and the panel title differ for `driver`
                                (`Drivers` vs `Driver Check-Ins`) and that pair is defined on
                                one row of one map — see lib/panelSubject.ts. */}
                            {SUBJECT_WORDS[s].chip}
                            {typeof n === 'number' && (
                                /* ⚠️ Tabular figures so the row does not jitter as counts
                                   change. ⛔ NO WEIGHT CLASS — the status counts dropped
                                   theirs on the same day, so neither row now has anything
                                   heavier than anything else in it. */
                                <span className="tabular-nums">{n}</span>
                            )}
                        </button>
                    </Fragment>
                );
            })}
        </div>
    );
}
